import { create } from 'zustand';
import { setAccessToken } from '../../lib/api.js';

export type Role = 'admin' | 'editor' | 'viewer';

export interface SessionUser {
  id: string;
  email: string;
  /** Legacy combined name, may still be present on older accounts. */
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  role: Role;
  organizationId: string;
}

/**
 * `rate_limited` is deliberately NOT a kind of `unauthenticated`.
 *
 * On the initial load there is no session yet — that request IS the attempt to
 * get one. A 429 there says "ask again shortly", not "you are logged out", but
 * it used to land in `clear()`, which routes to /login and fires another round
 * of requests into the bucket that was already full. That was the original
 * symptom this whole line of work started from.
 *
 * So it gets its own state: nothing is cleared, the user stays where they are,
 * and the gate offers a retry.
 */
export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'rate_limited';

interface AuthState {
  user: SessionUser | null;
  status: AuthStatus;
  /** Seconds the server asked us to wait, when it said. Only set with `rate_limited`. */
  retryAfterSeconds: number | null;
  setSession(token: string, user: SessionUser): void;
  clear(): void;
  setStatus(status: AuthStatus): void;
  /** Enter the retryable rate-limited state without touching the session. */
  setRateLimited(retryAfterSeconds: number | null): void;
  /** Back to `idle` so the bootstrap effect runs again. */
  retryBootstrap(): void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'idle',
  retryAfterSeconds: null,
  setSession(token, user) {
    setAccessToken(token);
    set({ user, status: 'authenticated', retryAfterSeconds: null });
  },
  clear() {
    setAccessToken(null);
    set({ user: null, status: 'unauthenticated', retryAfterSeconds: null });
  },
  setStatus(status) {
    set({ status });
  },
  setRateLimited(retryAfterSeconds) {
    // No setAccessToken(null), no user reset: being throttled is not a logout.
    set({ status: 'rate_limited', retryAfterSeconds });
  },
  retryBootstrap() {
    set({ status: 'idle', retryAfterSeconds: null });
  },
}));
